import type AdNetworkSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalFilters";
import type SignalAdNetwork from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/SignalAdNetwork";
import type { TranslatableNameWithType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import type { Translatable } from "app/pages/sales-intelligence/types";
import { filterSelectionTranslatable } from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";
import { isPartialFilterName } from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";
import { boldFilterSelectionTranslatable } from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";

import type Signal from "../../../models/core/Signal";
import type { SignalNameSegment, SignalNameSegments } from "../../../models/miscellaneous/SignalNameSegments";
import type UISignalNameBuilder from "../UISignalNameBuilder";

type FactoryDeps = {
    readonly adNetworks: readonly SignalAdNetwork[];
};

export default function createAdNetworkUISignalNameBuilder(
    deps: FactoryDeps,
): UISignalNameBuilder<AdNetworkSignalFilters> {
    const getAdNetworksFilterName = (filters: Partial<AdNetworkSignalFilters>): Readonly<TranslatableNameWithType> => {
        const nameBuilder = filterService.getFilterName(FilterType.AdNetworks);

        return nameBuilder.buildName(filters, deps.adNetworks);
    };

    const getAdNetworkLabels = (filters: Partial<AdNetworkSignalFilters>): readonly string[] => {
        const { adNetworkIds = [] } = filters;

        return deps.adNetworks.filter((adNetwork) => adNetworkIds.includes(adNetwork.id)).map(({ name }) => name);
    };

    const buildTypeSegment = (
        { signalType, filters }: Signal<AdNetworkSignalFilters>,
        adNetworksFilterName: Readonly<TranslatableNameWithType>,
    ): SignalNameSegment => {
        const signalI18nKeyTypeSegment = `si.signals.segments.${signalType}.${filters.action}`;

        return {
            key: signalI18nKeyTypeSegment,
            replacements: {
                adNetworks: filterSelectionTranslatable(adNetworksFilterName),
            },
        };
    };

    const buildDataSegment = ({ signalType, filters }: Signal<AdNetworkSignalFilters>): SignalNameSegment => {
        const signalI18nKeyDataSegment = `si.signals.segments.data.${signalType}.${filters.action}`;

        return {
            key: signalI18nKeyDataSegment,
        };
    };

    return {
        buildName({ signalType, filters }: Signal<AdNetworkSignalFilters>): Readonly<Translatable> {
            const signalI18nKey = `si.signals.${signalType}.${filters.action}`;
            const adNetworksFilterName = getAdNetworksFilterName(filters);

            return {
                key: signalI18nKey,
                replacements: {
                    adNetworks: boldFilterSelectionTranslatable(adNetworksFilterName),
                },
            };
        },
        buildNameSegments(signal: Signal<AdNetworkSignalFilters>): SignalNameSegments {
            const adNetworksFilterName = getAdNetworksFilterName(signal.filters);

            return {
                signalType: buildTypeSegment(signal, adNetworksFilterName),
                signalData: buildDataSegment(signal),
                ...(isPartialFilterName(adNetworksFilterName) ? { selectionOverflowOn: "signalType" as const } : {}),
            };
        },
        buildSelectionTooltipLabels(signal: Signal<AdNetworkSignalFilters>) {
            const adNetworksFilterName = getAdNetworksFilterName(signal.filters);

            if (!isPartialFilterName(adNetworksFilterName)) {
                return undefined;
            }

            const labels = getAdNetworkLabels(signal.filters);

            return labels.length > 1 ? { signalType: labels } : undefined;
        },
    };
}
